import { useMemo, useState, useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import {
  useUiPreferences,
  createUiPreference,
  updateUiPreference,
  deleteUiPreference,
} from "@api";
import { toast } from "@components/toast/Toast";

// ── Filter comparison ─────────────────────────────────────────────────
// Compares two filter sets for equality regardless of the order of filters.
// Returns true if both sets contain the same filters with the same values.

const filterValueEquals = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return a == b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    if (a.every((v) => typeof v !== "object")) {
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, i) => val === sortedB[i]);
    }
    return a.every((val, i) => filterValueEquals(val, b[i]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => filterValueEquals(a[k], b[k]));
  }
  return false;
};

export const areFiltersEqual = (filtersA, filtersB) => {
  if (!filtersA || !filtersB) return false;
  if (filtersA.length !== filtersB.length) return false;

  const sortedA = [...filtersA].sort((a, b) => a.key.localeCompare(b.key));
  const sortedB = [...filtersB].sort((a, b) => a.key.localeCompare(b.key));

  return sortedA.every(
    (f, i) =>
      f.key === sortedB[i].key &&
      f.type === sortedB[i].type &&
      filterValueEquals(f.value, sortedB[i].value),
  );
};

// ── Safe JSON parse for view values ──────────────────────────────────
// Returns the parsed filters array, or null if the value is malformed.

const safeParseViewValue = (value) => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Hook for saved views (named filter presets): list, save current filters
 * as a view, apply a saved view.
 * Persists views to the server via the UiPreferences API.
 *
 * Each view is stored as a UiPreference with:
 *   - type: `view:<configKey>`  (e.g. "view:tradeAccounts")
 *   - value: JSON-stringified filters array
 *
 * Only the active view's ID is stored in state; the full object is derived
 * from viewsList so it always reflects the latest data from the server.
 *
 * @param {string} configKey - Table config key (e.g. "tradeAccounts")
 * @param {Array} filters - Current active filters array
 * @param {function} setFilters - Setter for active filters (from useTableFilters)
 * @param {string}   [scope]   - Optional scope (e.g. groupId) – active view resets on change
 */
const useViewsManagement = (configKey, filters, setFilters, scope) => {
  const viewType = `view:${configKey}`;
  const posthog = usePostHog();

  // ── Analytics ───────────────────────────────────────────────────────
  const trackEvent = (action) => {
    posthog?.capture("saved_views_action", { action, configKey });
  };

  // ── State ────────────────────────────────────────────────────────────
  const { data: allPreferences } = useUiPreferences();
  const [viewsList, setViewsList] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [activeViewEdited, setActiveViewEdited] = useState(false);

  const [isSaveModalOpen, setSaveModalOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);

  // ── Derived: full active view object from the list ───────────────────
  const activeView = useMemo(
    () => viewsList.find((v) => v.id === activeViewId) ?? null,
    [viewsList, activeViewId],
  );

  // ── Reset active view when scope changes (e.g. switching groups) ────
  // Done during render so the reset is visible before effects fire.
  const prevScopeRef = useRef(scope);
  if (prevScopeRef.current !== scope) {
    prevScopeRef.current = scope;
    setActiveViewId(null);
    setActiveViewEdited(false);
  }

  // ── Populate views from shared preferences query ─────────────────────
  useEffect(() => {
    if (!allPreferences) return;
    setViewsList(allPreferences.filter((p) => p.type === viewType));
  }, [allPreferences, viewType]);

  // ── Auto-detect matching view ────────────────────────────────────────
  useEffect(() => {
    if (!activeViewId) {
      const matchingView = viewsList.find((v) => {
        const viewFilters = safeParseViewValue(v.value);
        return viewFilters && areFiltersEqual(filters, viewFilters);
      });
      setActiveViewId(matchingView?.id ?? null);
    } else if (filters.length === 0) {
      setActiveViewId(null);
      setActiveViewEdited(false);
    } else if (activeView) {
      const viewFilters = safeParseViewValue(activeView.value);
      setActiveViewEdited(
        !viewFilters || !areFiltersEqual(filters, viewFilters),
      );
    }
  }, [filters, viewsList, activeViewId, activeView]);

  // ── CRUD ─────────────────────────────────────────────────────────────

  const handleSaveView = async (name) => {
    const created = await createUiPreference({
      name,
      type: viewType,
      value: JSON.stringify(filters),
    });

    if (created) {
      setViewsList((prev) => [...prev, created]);
      setActiveViewId(created.id);
      toast({ description: `Created "${name}" Saved filter` });
    }
    setSaveModalOpen(false);
  };

  const applyView = (view) => {
    const parsedFilters = safeParseViewValue(view.value);
    if (!parsedFilters) return;
    setActiveViewId(view.id);
    setFilters(parsedFilters);
  };

  const removeFilters = () => {
    setActiveViewId(null);
    setFilters([]);
  };

  const handleUpdateView = async () => {
    if (!activeView) return;

    const newValue = JSON.stringify(filters);
    const success = await updateUiPreference(activeView.id, {
      name: activeView.name,
      value: newValue,
    });

    if (success) {
      setViewsList((prev) =>
        prev.map((v) =>
          v.id === activeView.id ? { ...v, value: newValue } : v,
        ),
      );
      toast({ description: `Updated "${activeView.name}" Saved filter` });
    }
  };

  const resetFilters = () => {
    if (!activeView) return;
    const parsedFilters = safeParseViewValue(activeView.value);
    if (!parsedFilters) return;
    setFilters(parsedFilters);
  };

  // ── Edit view (rename / delete) ─────────────────────────────────────

  const editView = (view) => {
    setEditingView(view);
  };

  const renameView = async (view, newName) => {
    const success = await updateUiPreference(view.id, {
      name: newName,
      value: view.value,
    });

    if (success) {
      setViewsList((prev) =>
        prev.map((v) => (v.id === view.id ? { ...v, name: newName } : v)),
      );
      toast({ description: `Renamed Saved filter to "${newName}"` });
    }

    setEditingView(null);
  };

  const deleteView = async (view) => {
    const success = await deleteUiPreference(view.id);

    if (success) {
      setViewsList((prev) => prev.filter((v) => v.id !== view.id));

      // Clear active if we deleted the active one
      if (activeViewId === view.id) {
        setActiveViewId(null);
        setActiveViewEdited(false);
      }

      toast({ description: `Deleted "${view.name}" Saved filter` });
    }

    setEditingView(null);
  };

  return {
    viewsList,
    activeView,
    activeViewEdited,
    removeFilters,
    isSaveModalOpen,
    setSaveModalOpen,
    handleSaveView,
    handleUpdateView,
    resetFilters,
    applyView,
    editingView,
    editView,
    renameView,
    deleteView,
    trackEvent,
  };
};

export default useViewsManagement;
